import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, fmtCurrency, fmtAmount } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowRightLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function ExchangePage() {
  const { t } = useLanguage();
  const { user, refresh } = useAuth();
  const [sp] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [from, setFrom] = useState(sp.get('from') || 'USD');
  const [to, setTo] = useState(sp.get('to') || 'EUR');
  const [amount, setAmount] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/wallet/overview').then(r => setOverview(r.data));
  useEffect(() => { load(); }, []);

  const currencies = overview?.currencies || ['USD', 'EUR', 'KMF', 'KZT'];
  const feePct = overview?.fee_percent ?? 1.5;

  useEffect(() => {
    const a = Number(amount);
    if (!a || from === to) { setPreview(null); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await api.post('/wallet/exchange/preview', { from_currency: from, to_currency: to, from_amount: a });
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 200);
    return () => clearTimeout(timer);
  }, [amount, from, to]);

  const canSubmit = user?.kyc_status === 'APPROVED';

  const execute = async () => {
    setBusy(true);
    try {
      const r = await api.post('/wallet/exchange/execute', { from_currency: from, to_currency: to, from_amount: Number(amount) });
      toast.success(`Exchanged ${fmtCurrency(r.data.from_amount, from)} → ${fmtCurrency(r.data.to_amount, to)}`);
      setConfirmOpen(false);
      setAmount('');
      setPreview(null);
      await load();
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Exchange failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">{t('wallet.exchange')}</h1>
      </div>
      {!canSubmit && (
        <div className="rounded-2xl border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/10 p-4 text-sm">
          {t('wallet.kyc_required')}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white" data-testid="exchange-widget">
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('wallet.from')}</Label>
                <Select value={from} onValueChange={setFrom}>
                  <SelectTrigger data-testid="exchange-from-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c} — {fmtAmount(overview?.balances?.[c])}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('wallet.to')}</Label>
                <Select value={to} onValueChange={setTo}>
                  <SelectTrigger data-testid="exchange-to-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{currencies.filter(c => c !== from).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('wallet.amount')} ({from})</Label>
              <Input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} data-testid="exchange-from-amount-input" className="tabular-nums" />
            </div>
            {preview && (
              <div className="rounded-xl bg-[hsl(var(--secondary))] px-4 py-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">{t('wallet.rate')}</span><span className="tabular-nums">1 {from} = {preview.rate.toFixed(6)} {to}</span></div>
                <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">{t('wallet.fee')} ({preview.fee_percent}%)</span><span className="tabular-nums">{fmtCurrency(preview.fee_amount, from)}</span></div>
                <div className="flex justify-between font-semibold"><span>{t('wallet.receive')}</span><span className="tabular-nums text-[hsl(var(--brand-navy))]">{fmtCurrency(preview.to_amount, to)}</span></div>
              </div>
            )}
            <Button onClick={() => setConfirmOpen(true)} disabled={!preview || !canSubmit || busy} data-testid="exchange-submit-button" className="w-full bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]"><ArrowRightLeft className="h-4 w-4 mr-2" />{t('wallet.execute')}</Button>
          </CardContent>
        </Card>

        <Card className="bg-white"><CardContent className="p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-lg">Rate sheet</h3>
            <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Fee: {feePct}%</p>
          <div className="mt-4 overflow-hidden rounded-xl border">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--secondary))]"><tr><th className="p-2 text-left">Pair</th><th className="p-2 text-right">Rate</th></tr></thead>
              <tbody>
                {Object.entries(overview?.rates || {}).map(([pair, rate]) => (
                  <tr key={pair} className="border-t"><td className="p-2">{pair.replace('_', ' → ')}</td><td className="p-2 text-right tabular-nums">{Number(rate).toFixed(6)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm exchange</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span>{t('wallet.from')}</span><span className="font-semibold">{fmtCurrency(preview.from_amount, from)}</span></div>
              <div className="flex justify-between"><span>{t('wallet.rate')}</span><span className="tabular-nums">1 {from} = {preview.rate.toFixed(6)} {to}</span></div>
              <div className="flex justify-between"><span>{t('wallet.fee')}</span><span className="tabular-nums">{fmtCurrency(preview.fee_amount, from)}</span></div>
              <div className="flex justify-between text-lg font-semibold pt-2 border-t"><span>{t('wallet.receive')}</span><span className="tabular-nums text-[hsl(var(--brand-navy))]">{fmtCurrency(preview.to_amount, to)}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={execute} disabled={busy} data-testid="exchange-confirm-button" className="bg-[hsl(var(--brand-gold))] hover:bg-[hsl(43_74%_46%)] text-[hsl(var(--brand-ink))]">{t('common.confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
